import type { CheckoutJourney } from '~~/layers/base/types/checkout'

/**
 * Checkout journey policy — single source of truth for which `journey` values mean what.
 *
 * Shared by server checkout routes and client store (no server-only imports).
 */

/**
 * Handwriting product journeys on checkout — **always guest ACCS** (signed-in or not).
 *
 * Used by {@link isHandwritingCheckoutJourney} and as the handwriting subset of the checkout API allowlist.
 */
export const HANDWRITING_CHECKOUT_JOURNEYS = [
    'handwriting_stationery',
    'handwritten_note',
    'handwritten_note_bulk',
] as const satisfies readonly CheckoutJourney[]

/**
 * Journeys accepted on `POST /api/checkout/*` today.
 *
 * Same as {@link HANDWRITING_CHECKOUT_JOURNEYS} until `ecommerce_stationery` ships on the checkout
 * API. Adding ecommerce here also re-enables personalize prepare-cart pre-warm (TK-13531 /
 * `shouldPreWarmOnLoad`).
 */
export const CHECKOUT_API_JOURNEYS = HANDWRITING_CHECKOUT_JOURNEYS

/** Union of {@link HANDWRITING_CHECKOUT_JOURNEYS} labels for typed guards and routing. */
export type HandwritingCheckoutJourney = (typeof HANDWRITING_CHECKOUT_JOURNEYS)[number]

export function isHandwritingCheckoutJourney(
    journey: CheckoutJourney
): journey is HandwritingCheckoutJourney {
    return (HANDWRITING_CHECKOUT_JOURNEYS as readonly CheckoutJourney[]).includes(journey)
}

export function isCheckoutApiJourney(journey: CheckoutJourney): boolean {
    return (CHECKOUT_API_JOURNEYS as readonly CheckoutJourney[]).includes(journey)
}

/**
 * Whether `journey` is handwritten note send (single or bulk).
 *
 * Note checkout uses hidden recipient shipping — not user-facing Stripe delivery persistence.
 */
export function isHandwrittenNoteJourney(journey: CheckoutJourney | null | undefined): boolean {
    return journey === 'handwritten_note' || journey === 'handwritten_note_bulk'
}

/**
 * Journeys where Stripe billing address edits seed profile return addresses (TK-12335).
 *
 * Formerly `shouldPersistStripeDeliveryToProfile` — renamed with `persistStripeBillingAddress`
 * so “delivery” is not read as recipient/shipping.
 *
 * Note send flows are included: the payer's *billing* address is a separate concern from the
 * recipient's hidden *shipping* (delivery) address — `syncReturnAddressOnGuestCart`'s own
 * `shouldSyncReturnAddressToShipping` gate already keeps recipient shipping untouched for notes.
 * Widen further when `ecommerce_stationery` ships on checkout API.
 */
export function shouldPersistStripeBillingAddressToProfile(
    journey: CheckoutJourney | null | undefined
): boolean {
    return journey === 'handwriting_stationery' || isHandwrittenNoteJourney(journey)
}
